import { Checkbox } from '@almbrand/checkbox';
import { LoadingSpinner } from '@almbrand/loadingspinner';
import { SelectField } from '@almbrand/selectfield';
import classNames from 'classnames';
import { ThemeNames } from 'constantsValues';
import { PageContext } from 'context/PageContext';
import { useSpdSearch } from 'hooks';
import { useContext, useEffect, useRef, useState } from 'react';
import { FieldValues, UseFormRegister, UseFormTrigger } from 'react-hook-form';
import { Obj } from 'src/hooks/useSpdSearch';
import styles from './CarSearch.module.scss';
import { LicensePlateInput } from './LicensePlateInput';
import { ResultBox } from './ResultBox';

type OptionsState = {
	licensePlate: { errorMessage?: string; options: Option[] };
	brand: { errorMessage?: string; options: Option[] };
	year: { errorMessage?: string; options: Option[] };
	model: { errorMessage?: string; options: Option[] };
	variant: { errorMessage?: string; options: Option[] };
};

export type Option = {
	value: string;
	label: string;
};

export type ResultBoxProps = {
	header: string;
	subHeader: string;
};

export interface CarSearchProps {
	register: UseFormRegister<FieldValues>;
	trigger?: UseFormTrigger<FieldValues>;
	submitStep?: () => void;
	contentType: string;
	stepContentId: number;
	onComplete?: (licensePlate: string) => void;
	resultBox?: ResultBoxProps | null;
	licensePlateSearchField: SpdSearchType;
	makeSearchField: SpdSearchType;
	yearSearchField: SpdSearchType;
	modelSearchField: SpdSearchType;
	variantSearchField: SpdSearchType;
}

export const CarSearch: React.FC<CarSearchProps> = ({
	register,
	trigger,
	submitStep,
	contentType,
	stepContentId,
	onComplete,
	licensePlateSearchField,
	makeSearchField,
	yearSearchField,
	modelSearchField,
	variantSearchField,
	...props
}) => {
	const pageContext = useContext(PageContext);
	const { theme } = pageContext ?? {};
	const themeName = ThemeNames[theme];
	const [makeAndModelState, setMakeAndModelState] = useState<
		{ name: string; param: { param_name: string; param_value: string } }[]
	>([]);
	const licensePlateSearch = useSpdSearch({ spdSearchType: licensePlateSearchField });
	const makeSearch = useSpdSearch({ spdSearchType: makeSearchField });
	const yearSearch = useSpdSearch({ spdSearchType: yearSearchField });
	const modelSearch = useSpdSearch({ spdSearchType: modelSearchField });
	const variantSearch = useSpdSearch({ spdSearchType: variantSearchField });
	const [showSpinner, setShowSpinner] = useState(false);
	const [showResult, setShowResult] = useState(false);
	const [noLicensePlate, setNoLicensePlate] = useState(false);
	const [resultBox, setResultBox] = useState<ResultBoxProps | null>(null);
	const licensePlateDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const [options, setOptions] = useState<OptionsState>({
		licensePlate: { options: [] },
		brand: { options: [] },
		year: { options: [] },
		model: { options: [] },
		variant: { options: [] },
	});
	const [selectFieldKeys, setSelectFieldKeys] = useState({
		model: 0,
		variant: 0,
		year: 0,
	});

	const handleLicensePlateChange = async (formatted: string, isComplete: boolean) => {
		// Remove all spaces and make lower case before sending to parent
		const normalized = formatted.replace(/\s+/g, '').toLowerCase();

		if (licensePlateDebounceRef.current) {
			clearTimeout(licensePlateDebounceRef.current);
			licensePlateDebounceRef.current = null;
		}

		if (isComplete) {
			setShowSpinner(true);
			setShowResult(false);

			licensePlateDebounceRef.current = setTimeout(async () => {
				const result = await licensePlateSearch.doSearch({
					name: licensePlateSearchField.searchConfiguration.spdSearchConfigurationName,
					params: [
						{
							param_name:
								licensePlateSearchField.searchConfiguration.inputFields.items[0].content.param || '',
							param_value: normalized.toLocaleUpperCase(),
						},
						{
							param_name: 'USER_ID',
							param_value: ' ',
						},
					],
				});

				setResultBox({
					header: `${(result as Obj).make} ${(result as Obj).model}`,
					subHeader: `${(result as Obj).modelYear} ${(result as Obj).variant}`,
				});

				saveFieldChangeToForm(register, licensePlateSearchField, result as Obj, contentType);
			}, 500);

			onComplete?.(normalized);
		} else {
			setShowSpinner(false);
			setShowResult(false);
		}
	};

	useEffect(() => {
		return () => {
			if (licensePlateDebounceRef.current) {
				clearTimeout(licensePlateDebounceRef.current);
			}
		};
	}, []);

	// Show ResultBox and hide spinner when resultBox is set from parent
	useEffect(() => {
		if (resultBox) {
			setShowResult(true);
			setShowSpinner(false);
		}
	}, [resultBox]);

	useEffect(() => {
		// Only run if the last updated field was the brand (makeSearchField)
		const last = makeAndModelState[makeAndModelState.length - 1];
		if (last && last.name === makeSearchField.uniqueIdentifier.uid) {
			populateModelOptions();
		}

		if (last && last.name === modelSearchField.uniqueIdentifier.uid) {
			populateVariantOptions();
		}

		if (last && last.name === variantSearchField.uniqueIdentifier.uid) {
			//populateYearOptions();
			saveYearToForm();
		}
	}, [makeAndModelState]);

	const clearOptionGroups = (keys: Array<'model' | 'variant' | 'year'>) => {
		setOptions((prev) => {
			const next = { ...prev };
			keys.forEach((key) => {
				next[key] = { ...prev[key], options: [] };
			});
			return next;
		});

		setSelectFieldKeys((prev) => {
			const next = { ...prev };
			keys.forEach((key) => {
				next[key] = prev[key] + 1;
			});
			return next;
		});
	};

	const updateMakeAndModelState = (
		setState: React.Dispatch<
			React.SetStateAction<{ name: string; param: { param_name: string; param_value: string } }[]>
		>,
		field: SpdSearchType,
		value: Option
	) => {
		setState((state) => {
			const existingIndex = state.findIndex((item) => item.name === field.uniqueIdentifier.uid);
			const paramName = field.searchConfiguration.outputFields.items[0].content.param;
			if (existingIndex !== -1) {
				// Update the existing item and remove all items after it
				const updated = state
					.slice(0, existingIndex + 1)
					.map((item, index) =>
						index === existingIndex ? { ...item, param: { ...item.param, param_value: value.value } } : item
					);

				return updated;
			}
			// If not found, just add the new item
			return [
				...state,
				{
					name: field.uniqueIdentifier.uid,
					param: {
						param_name: paramName,
						param_value: value.value,
					},
				},
			];
		});
	};

	const saveFieldChangeToForm = (
		register: UseFormRegister<FieldValues>,
		field: SpdSearchType,
		value: Option | Obj,
		contentType: string
	) => {
		field.searchConfiguration.outputFields.items.forEach((outputField) => {
			const { onChange, name } = register(outputField.content.uniqueIdentifier.uid, { required: true });
			const mappedValue = value.value ?? value[outputField.content.param];

			onChange({
				target: {
					name: name,
					value: {
						id: name,
						value: mappedValue,
						type: contentType,
					},
				},
			});
		});
	};

	const handleCheckboxChange = async () => {
		setNoLicensePlate(!noLicensePlate);

		if (!noLicensePlate) {
			populateBrandOptions();
		}
	};

	const handleBrandChange = (value: Option): void => {
		clearOptionGroups(['model', 'variant', 'year']);
		updateMakeAndModelState(setMakeAndModelState, makeSearchField, value);
		saveFieldChangeToForm(register, makeSearchField, value, contentType);
	};

	const handleYearChange = (value: Option): void => {
		updateMakeAndModelState(setMakeAndModelState, yearSearchField, value);
		saveFieldChangeToForm(register, yearSearchField, value, contentType);
	};

	const handleModelChange = (value: Option): void => {
		clearOptionGroups(['variant', 'year']);
		updateMakeAndModelState(setMakeAndModelState, modelSearchField, value);
		saveFieldChangeToForm(register, modelSearchField, value, contentType);
	};

	const handleVariantChange = (value: Option): void => {
		updateMakeAndModelState(setMakeAndModelState, variantSearchField, value);
		saveFieldChangeToForm(register, variantSearchField, value, contentType);
	};

	// Utility to build params array for a given searchField
	const buildParams = (
		searchField: SpdSearchType,
		makeAndModelState: { name: string; param: { param_name: string; param_value: string } }[]
	) => {
		return searchField.searchConfiguration.inputFields.items.map((item) => {
			const found = makeAndModelState.find((state) => state.name === item.content.uniqueIdentifier.uid);
			return found ? found.param : { param_name: item.content.param, param_value: '' };
		});
	};

	const [loading, setLoading] = useState({
		brand: false,
		year: false,
		model: false,
		variant: false,
	});

	const setLoadingKey = (key: keyof typeof loading, value: boolean) => {
		setLoading((prev) => ({ ...prev, [key]: value }));
	};

	const populateBrandOptions = async () => {
		setLoadingKey('brand', true);
		const result = await makeSearch.doSearch({
			name: makeSearchField.searchConfiguration.spdSearchConfigurationName,
			params: [],
		});
		if (result) {
			setLoadingKey('brand', false);
			setOptionsFromSearchResult('brand', result);
		}
	};

	const populateYearOptions = async () => {
		setLoadingKey('year', true);
		const params = buildParams(yearSearchField, makeAndModelState);
		const result = await yearSearch.doSearch({
			name: yearSearchField.searchConfiguration.spdSearchConfigurationName,
			params,
		});
		if (result) {
			setLoadingKey('year', false);
			setOptionsFromSearchResult('year', result);
		}
	};

	const saveYearToForm = async () => {
		const params = buildParams(yearSearchField, makeAndModelState);
		const result = await yearSearch.doSearch({
			name: yearSearchField.searchConfiguration.spdSearchConfigurationName,
			params,
		});
		if (result) {
			saveFieldChangeToForm(register, yearSearchField, result as Obj, contentType);
		}
	};

	const populateModelOptions = async () => {
		setLoadingKey('model', true);
		let params = buildParams(modelSearchField, makeAndModelState);
		const result = await modelSearch.doSearch({
			name: modelSearchField.searchConfiguration.spdSearchConfigurationName,
			params,
		});
		if (result) {
			setLoadingKey('model', false);
			setOptionsFromSearchResult('model', result);
		}
	};

	const populateVariantOptions = async () => {
		setLoadingKey('variant', true);
		let params = buildParams(variantSearchField, makeAndModelState);
		const result = await variantSearch.doSearch({
			name: variantSearchField.searchConfiguration.spdSearchConfigurationName,
			params,
		});
		if (result) {
			setLoadingKey('variant', false);
			setOptionsFromSearchResult('variant', result);
		}
	};

	const setOptionsFromSearchResult = (
		key: keyof Pick<OptionsState, 'brand' | 'year' | 'model' | 'variant'>,
		result: Obj | Option[]
	) => {
		setOptions((prev) => ({
			...prev,
			[key]: {
				...prev[key],
				options: Array.isArray(result) ? result : [],
			},
		}));
	};

	return (
		<div className={classNames(styles.CarSearch)}>
			{!noLicensePlate && <LicensePlateInput onChange={handleLicensePlateChange} />}

			<div className={classNames(styles.CarSearch__checkbox)}>
				<Checkbox
					name='checkbox'
					label='Jeg kender ikke min nummerplade'
					roundedCorners
					onChange={handleCheckboxChange}
				/>
			</div>
			{showSpinner && !noLicensePlate && (
				<div className={classNames(styles.CarSearch__spinner)}>
					<LoadingSpinner
						color='primary'
						style='small'
					/>
				</div>
			)}

			{showResult && resultBox && !noLicensePlate && <ResultBox {...resultBox} />}

			{noLicensePlate && (
				<>
					<div className={classNames(styles.CarSearch__selectField, styles.CarSearch__selectFieldBrand)}>
						<SelectField
							themeName={themeName}
							options={options.brand.options}
							errorMessage={options.brand.errorMessage}
							icon={{ isIcon: false }}
							placeHolder='Bilmærke'
							isSearchable
							iconPosition='right'
							onChange={handleBrandChange}
							isLoading={loading.brand}
						/>
					</div>
					<div className={classNames(styles.CarSearch__selectField, styles.CarSearch__selectFieldModel)}>
						<SelectField
							themeName={themeName}
							key={`model-${selectFieldKeys.model}`}
							options={options.model.options}
							isDisabled={!options.model.options.length}
							errorMessage={options.model.errorMessage}
							icon={{ isIcon: false }}
							placeHolder='Model'
							isSearchable
							iconPosition='right'
							onChange={handleModelChange}
							isLoading={loading.model}
						/>
					</div>
					<div className={classNames(styles.CarSearch__selectField, styles.CarSearch__selectFieldVariant)}>
						<SelectField
							themeName={themeName}
							key={`variant-${selectFieldKeys.variant}`}
							options={options.variant.options}
							isDisabled={!options.model.options.length}
							errorMessage={options.variant.errorMessage}
							icon={{ isIcon: false }}
							placeHolder='Variant'
							isSearchable
							iconPosition='right'
							onChange={handleVariantChange}
							isLoading={loading.variant}
						/>
					</div>
					{/* <SelectField
						options={options.year.options}
						isDisabled={!options.brand.options.length}
						errorMessage={options.year.errorMessage}
						icon={{ isIcon: false }}
						placeHolder='Årgang'
						isSearchable
						iconPosition='right'
						onChange={handleYearChange}
						isLoading={loading.year}
					/> */}
				</>
			)}
		</div>
	);
};
